/**
 * Price Analysis utility (Task-032).
 * Pure calculation: effective price, commission, cost, profit with storewide discount.
 * No DB writes; used for read-only analysis only.
 */

export type ProfitStatus = 'normal' | 'low' | 'negative';

export interface PriceAnalysisInput {
  /** Base selling price (selling_price if set, else original_price) */
  basePrice: number;
  /** Average cost from product_costs */
  averageCost: number;
  /** Commission rate from categories.standard_delivery_commission as decimal (e.g. 0.18 for 18%) */
  commissionRate: number;
  /** User input: percentage off (e.g. 20 = 20% off = 8折) */
  inputDiscount: number;
}

export interface PriceAnalysisResult {
  effectivePrice: number;
  commissionFee: number;
  realCost: number;
  finalProfit: number;
  profitStatus: ProfitStatus;
}

/**
 * Compute effective price, commission, real cost, final profit and profit status.
 * Formula:
 *   effectivePrice = basePrice * (1 - inputDiscount/100)
 *   commissionFee = effectivePrice * commissionRate
 *   realCost = averageCost + commissionFee
 *   finalProfit = effectivePrice - realCost
 *   profitStatus: normal > 10, low 0..10, negative < 0
 */
export function calculatePriceAnalysis(input: PriceAnalysisInput): PriceAnalysisResult {
  const { basePrice, averageCost, commissionRate, inputDiscount } = input;
  const discountPct = Math.max(0, Math.min(100, inputDiscount)) / 100;
  const effectivePrice = basePrice * (1 - discountPct);
  const commissionFee = effectivePrice * (commissionRate ?? 0);
  const realCost = (averageCost ?? 0) + commissionFee;
  const finalProfit = effectivePrice - realCost;

  let profitStatus: ProfitStatus = 'normal';
  if (finalProfit < 0) profitStatus = 'negative';
  else if (finalProfit <= 10) profitStatus = 'low';

  return {
    effectivePrice,
    commissionFee,
    realCost,
    finalProfit,
    profitStatus,
  };
}
